import {
  Component,
  input,
  InputSignal,
  model,
  ModelSignal,
  output,
  OutputEmitterRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslateModule } from '@ngx-translate/core';
import { CheckboxComponent } from '@shared/components/ui/checkbox';
import { SwitchComponent } from '@shared/components/ui/switch';
import { SystemIconsComponent, ISystemIcons } from '@shared/components/ui/system-icons';

export type TCheckboxConfigListItem = {
  checked: boolean;
  partialSelected?: boolean;
}

/**
 * A reusable list item component designed for use in dropdowns or lists.
 * Displays text with optional left and right icons, and supports selection and disabled states.
 *
 * @component
 * @selector lib-list-item
 *
 * @example
 * <lib-list-item
 *   [text]="'Option 1'"
 *   [leftIcon]="'fi fi-rr-star'"
 *   [rightIcon]="'fi fi-rr-info'"
 *   [selected]="true"
 *   [disabled]="false"
 *   (onSelectedChange)="handleSelection()"
 * ></lib-list-item>
 */
@Component({
  selector: 'lib-list-item',
  imports: [
    CommonModule,
    TranslateModule,
    CheckboxComponent,
    SwitchComponent,
    SystemIconsComponent
  ],
  templateUrl: './list-item.component.html',
  styleUrl: './list-item.component.scss',
})
export class ListItemComponent {
  /**
   * The text to display in the list item.
   * @type {InputSignal<string>}
   * @default ''
   */
  public text: InputSignal<string> = input<string>('');

  /**
   * The secondary text to display in the list item, typically shown below the main text.
   * @type {InputSignal<string>}
   * @default ''
   */
  public secondaryText: InputSignal<string> = input<string>('');

  /**
   * CSS class for an optional icon displayed to the left of the text.
   * Typically uses an icon font like Font Awesome or a custom icon set.
   * @type {InputSignal<string>}
   * @default ''
   * @example 'fi fi-rr-star'
   */
  public leftIcon: InputSignal<string> = input<string>('');

  public systemLeftIcon: InputSignal<ISystemIcons['iconName']> = input<ISystemIcons['iconName']>(null);

  public systemRightIcon: InputSignal<ISystemIcons['iconName']> = input<ISystemIcons['iconName']>(null);

  /**
   * Scale factor for the system icon (left). Applied via SystemIconsComponent.
   * @default 1
   */
  public iconScale: InputSignal<number> = input<number>(1);
  
  public checkbox: InputSignal<TCheckboxConfigListItem> = input<TCheckboxConfigListItem>(null);


  /**
   * CSS class for an optional icon displayed to the right of the text.
   * Typically uses an icon font like Font Awesome or a custom icon set.
   * @type {InputSignal<string>}
   * @default ''
   */
  public readonly displaySelectedIcon: InputSignal<boolean> = input<boolean>(true);

  /**
   * CSS class for an optional icon displayed to the right of the text.
   * Only shown if the item is not selected.
   * @type {InputSignal<string>}
   * @default ''
   * @example 'fi fi-rr-info'
   */
  public rightIcon: InputSignal<string> = input<string>('');

  /**
   * Indicates whether the list item is currently selected.
   * When true, a checkmark icon is displayed on the right.
   * @type {InputSignal<boolean>}
   * @default false
   */
  public selected: InputSignal<boolean> = input<boolean>(false);

  public switch: InputSignal<boolean> = input<boolean>(false);

  /**
   * The value of the switch, if the item is a switch.
   * @type {ModelSignal<boolean>}
   * @default false
   */
  public switchValue: ModelSignal<boolean> = model<boolean>(false);

  /**
   * Disables the list item, preventing interaction and applying a disabled style.
   * @type {InputSignal<boolean>}
   * @default false
   */
  public disabled: InputSignal<boolean> = input<boolean>(false);

  /**
   * Emits an event when the list item is clicked, unless disabled.
   * @type {OutputEmitterRef<void>}
   */
  public onSelectedChange: OutputEmitterRef<void> = output<void>();

  /**
   * Handles the click event on the list item.
   * Emits the `onSelectedChange` event if the item is not disabled.
   * @returns {void}
   */
  public onItemClicked(): void {
    if (!this.disabled()) {
      this.onSelectedChange.emit();
    }
  }
}
